import { FC, ReactNode, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as ArrowDownSvg } from 'src/assets/svg/arrow-down.svg'
import { ReactComponent as GlobeSvg } from 'src/assets/svg/Globe.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { Skeleton } from 'src/components/Skeleton'
import { CommentItem } from 'src/charts/CommentsChartSection/CommentItem'
import { CampaignComment } from 'src/reactQuery/useGetCampaignCommentsQuery'
// The loading rows reuse the real CommentItem stylesheet, as CommentsList does.
import itemStyles from 'src/charts/CommentsChartSection/CommentItem/commentItem.module.scss'
import styles from './campaignCommentsList.module.scss'

export type SentimentTab = 'all' | 'positive' | 'neutral' | 'negative'

const COMMENT_SKELETON_COUNT = 4
const COMMENT_HEADER_HEIGHT = 16
const COMMENT_AUTHOR_WIDTH = 116
const COMMENT_DATE_WIDTH = 74
const COMMENT_TEXT_HEIGHT = 22

const TABS: { label: string; value: SentimentTab }[] = [
  { label: 'All', value: 'all' },
  { label: 'Positive', value: 'positive' },
  { label: 'Neutral', value: 'neutral' },
  { label: 'Negative', value: 'negative' },
]

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

interface Option {
  icon?: ReactNode
  label: string
  value: string
}

interface DropdownProps {
  onChange: (value: string) => void
  options: Option[]
  value: string
}

// Same look as the Content Analysis platform select.
const Dropdown: FC<DropdownProps> = ({ onChange, options, value }) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useOutsideClick({ onOutsideClick: () => setIsOpen(false), ref })

  const selected = options.find((o) => o.value === value) || options[0]

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <button
        className={classNames(styles.select, { [styles.open]: isOpen })}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        {selected?.icon && (
          <span className={styles.optionIcon}>{selected.icon}</span>
        )}
        <span className={styles.selectLabel}>{selected?.label}</span>
        <ArrowDownSvg
          className={classNames(styles.dropdownArrow, {
            [styles.open]: isOpen,
          })}
        />
      </button>
      {isOpen && (
        <div className={styles.dropdown}>
          {options.map((option) => (
            <button
              className={classNames(styles.option, {
                [styles.selected]: option.value === value,
              })}
              key={option.value}
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
              type="button"
            >
              {option.icon && (
                <span className={styles.optionIcon}>{option.icon}</span>
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface CampaignCommentsListProps {
  activeTab: SentimentTab
  comments: CampaignComment[]
  isLoading?: boolean
  onPlatformChange: (platform: string) => void
  onTabChange: (tab: SentimentTab) => void
  // Opens Post Details for the mention a comment belongs to.
  onViewThreadDetails?: (threadId: string) => void
  platforms: string[]
  selectedPlatform: string
}

export const CampaignCommentsList: FC<CampaignCommentsListProps> = ({
  activeTab,
  comments,
  isLoading = false,
  onPlatformChange,
  onTabChange,
  onViewThreadDetails,
  platforms,
  selectedPlatform,
}) => {
  const platformOptions = useMemo<Option[]>(
    () => [
      { icon: <GlobeSvg />, label: 'All Platforms', value: 'all' },
      ...platforms.map((p) => ({
        icon: SOCIAL_ICONS[p],
        label: platformDisplayLabel(p, capitalize(p)),
        value: p,
      })),
    ],
    [platforms]
  )

  return (
    <div className={styles.commentsList}>
      <div className={styles.header}>
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              className={classNames(styles.tab, {
                [styles.active]: activeTab === tab.value,
              })}
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.filters}>
          <Dropdown
            onChange={onPlatformChange}
            options={platformOptions}
            value={selectedPlatform}
          />
        </div>
      </div>

      <div className={styles.list}>
        {isLoading ? (
          Array.from({ length: COMMENT_SKELETON_COUNT }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={itemStyles.commentItem} key={index}>
              <div className={itemStyles.header}>
                <div className={itemStyles.platformInfo}>
                  <Skeleton circle width={COMMENT_HEADER_HEIGHT} />
                  <Skeleton
                    height={COMMENT_HEADER_HEIGHT}
                    width={COMMENT_AUTHOR_WIDTH}
                  />
                </div>
                <Skeleton
                  height={COMMENT_HEADER_HEIGHT}
                  width={COMMENT_DATE_WIDTH}
                />
              </div>
              <Skeleton height={COMMENT_TEXT_HEIGHT} width="100%" />
            </div>
          ))
        ) : comments.length === 0 ? (
          <div className={styles.emptyState}>No comments found</div>
        ) : (
          comments.map((comment) => (
            <CommentItem
              comment={comment}
              key={comment.id}
              onViewDetails={
                onViewThreadDetails && comment.threadId
                  ? () => onViewThreadDetails(comment.threadId as string)
                  : undefined
              }
            />
          ))
        )}
      </div>
    </div>
  )
}
